import { ArrowRight, Shield, FileCheck, Scale, Building2 } from "lucide-react";

const serviceHighlights = [
  {
    icon: Shield,
    title: "Title Verification for Diaspora Investors",
    description: "We conduct independent land registry searches and document reviews to confirm legal ownership before any payment is made."
  },
  {
    icon: FileCheck,
    title: "Property Due Diligence",
    description: "Comprehensive investigation into property history, encumbrances, and ownership records to ensure a secure investment."
  },
  {
    icon: Scale,
    title: "Transaction Structuring",
    description: "Legal structuring and negotiation support to ensure proper documentation and enforceable agreements."
  },
  {
    icon: Building2,
    title: "Secure Acquisition Oversight",
    description: "We guide clients from offer stage through closing to ensure that property transactions are completed safely and legally."
  }
];

export const DiasporaAdvisorySection = () => {
  return (
    <section 
      className="py-16 md:py-24 lg:py-32 bg-[#0A0A0B] relative overflow-hidden"
      data-testid="diaspora-advisory-section"
    >
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent" />
      
      {/* Decorative corner accents */}
      <div className="absolute top-12 left-12 w-24 h-24 border-l border-t border-[#D4AF37]/15 hidden lg:block" />
      <div className="absolute bottom-12 right-12 w-24 h-24 border-r border-b border-[#D4AF37]/15 hidden lg:block" />
      
      <div className="relative z-10 max-w-6xl mx-auto px-5 md:px-8 lg:px-12">
        {/* Header */}
        <div className="text-center mb-10 md:mb-14 lg:mb-16">
          <span className="text-[#D4AF37] text-xs md:text-sm tracking-[0.2em] md:tracking-[0.3em] uppercase font-semibold">
            For Nigerians Abroad
          </span>
          <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl lg:text-4xl xl:text-5xl text-white mt-4 md:mt-6 leading-tight">
            Legal Advisory for
            <span className="block text-[#D4AF37] italic mt-2">Diaspora Property Investors</span>
          </h2>
          <div className="w-16 md:w-20 h-px bg-[#D4AF37] mx-auto mt-6 md:mt-8" />
        </div>

        {/* Introduction Text */}
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <p className="text-white/70 text-sm md:text-base lg:text-lg leading-[1.9] md:leading-[2] mb-6">
            Nigerians living abroad frequently face significant risks when purchasing property in Nigeria 
            due to fraudulent titles, unclear ownership records, and lack of reliable transaction oversight.
          </p>
          <p className="text-white/70 text-sm md:text-base lg:text-lg leading-[1.9] md:leading-[2]">
            4 ACES Attorneys provides specialised legal advisory services designed to protect diaspora 
            investors throughout the property acquisition process.
          </p>
        </div>

        {/* Service Highlights Grid */}
        <div className="grid md:grid-cols-2 gap-6 md:gap-8 mb-12 md:mb-16">
          {serviceHighlights.map((service, index) => (
            <div 
              key={index}
              className="group p-6 md:p-8 border border-[#D4AF37]/20 hover:border-[#D4AF37]/50 bg-[#0A0A0B]/50 transition-all duration-300"
              data-testid={`diaspora-service-${index}`}
            >
              <div className="flex items-start gap-4 md:gap-5">
                <div className="w-12 h-12 md:w-14 md:h-14 border border-[#D4AF37]/30 flex items-center justify-center flex-shrink-0 group-hover:border-[#D4AF37] group-hover:bg-[#D4AF37]/10 transition-all duration-300">
                  <service.icon className="w-5 h-5 md:w-6 md:h-6 text-[#D4AF37]" />
                </div>
                <div>
                  <h3 className="font-['Playfair_Display'] text-lg md:text-xl text-white mb-2 md:mb-3">
                    {service.title}
                  </h3>
                  <p className="text-white/60 text-sm md:text-base leading-relaxed">
                    {service.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Closing Paragraph */}
        <div className="text-center max-w-3xl mx-auto mb-10 md:mb-12">
          <p className="text-white/70 text-sm md:text-base lg:text-lg leading-[1.9] md:leading-[2]">
            Our firm regularly advises diaspora clients based in the United Kingdom, United States, 
            Canada, and the Middle East who are seeking secure property investments in Nigeria.
          </p>
        </div>

        {/* CTA Button */}
        <div className="text-center">
          <a
            href="#booking"
            className="inline-flex items-center gap-2 md:gap-3 bg-[#D4AF37] text-[#0A0A0B] px-8 md:px-10 py-4 md:py-5 font-semibold text-sm tracking-wide hover:bg-[#C5A028] transition-colors"
            data-testid="diaspora-advisory-cta"
          >
            Book a Private Consultation
            <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    </section>
  );
};
